import { useState, useRef, useEffect } from "react";
import { Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { AIChatService } from "@/services/api/AIChatService";

interface PromptEnhanceButtonProps {
  currentPrompt: string;
  onPromptEnhanced: (enhancedPrompt: string) => void;
  agentName?: string;
  agentDescription?: string;
  disabled?: boolean;
  size?: "sm" | "default" | "icon";
  label?: string;
  className?: string;
}

export const PromptEnhanceButton = ({
  currentPrompt,
  onPromptEnhanced,
  agentName,
  agentDescription,
  disabled = false,
  size = "icon",
  label,
  className,
}: PromptEnhanceButtonProps) => {
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [showInstructionsInput, setShowInstructionsInput] = useState(false);
  const [userInstructions, setUserInstructions] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showInstructionsInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showInstructionsInput]);

  const handleButtonClick = () => {
    if (!currentPrompt.trim()) {
      toast.error("Please enter a prompt to enhance");
      return;
    }

    if (!showInstructionsInput) {
      setShowInstructionsInput(true);
    }
  };

  const handleEnhance = async () => {
    setIsEnhancing(true);

    const loadingToast = toast.loading("Generating enhanced prompt...");

    try {
      const enhancedPrompt = await AIChatService.enhancePrompt({
        currentPrompt,
        agentName,
        agentDescription,
        userInstructions: userInstructions.trim() || undefined,
      });

      onPromptEnhanced(enhancedPrompt.trim());
      toast.success("Prompt enhanced successfully!", { id: loadingToast });
      setShowInstructionsInput(false);
      setUserInstructions("");
    } catch (error) {
      console.error("Error enhancing prompt:", error);
      toast.error("Failed to enhance prompt. Please try again.", { id: loadingToast });
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleCancel = () => {
    setShowInstructionsInput(false);
    setUserInstructions("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleEnhance();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  return (
    <div className="relative inline-block">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant={label ? "ghost" : "ghost"}
              size={label ? "default" : size}
              onClick={handleButtonClick}
              disabled={disabled || isEnhancing || !currentPrompt.trim()}
              className={`group relative overflow-hidden bg-white rounded-full shadow-md hover:shadow-lg transition-all duration-300 hover:bg-genie-primary/10 hover:text-genie-primary ${
                isEnhancing ? 'text-genie-primary' : 'text-gray-600'
              } ${label ? 'h-8 px-3' : 'h-8 w-8'} ${className || ''}`}
            >
              <div className="flex items-center">
                {!label && <Sparkles className={`h-4 w-4 ${isEnhancing ? 'animate-spin' : ''}`} />}
                {label && <span className="text-sm">{label}</span>}
              </div>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isEnhancing ? 'Generating...' : (label || 'Enhance prompt')}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {showInstructionsInput && (
        <div className="absolute top-full mt-2 right-0 z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-3 min-w-[320px]">
          <div className="space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Enhancement instructions (optional)
                </label>
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder="e.g., Make it more professional, add humor, etc."
                  value={userInstructions}
                  onChange={(e) => setUserInstructions(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isEnhancing}
                  className="text-sm"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleCancel}
                disabled={isEnhancing}
                className="ml-2 h-6 w-6"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={isEnhancing}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleEnhance}
                disabled={isEnhancing}
                className="bg-genie-primary hover:bg-genie-primary/90"
              >
                {isEnhancing ? "Enhancing..." : "Enhance"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};